import { formatMoney, money, multiplyByRatio } from "@stb/core/money"
import { CENTS_PER_KM_CAP_KM, centsPerKmRate } from "@stb/jurisdiction-au"
import { formatAud, PRODUCT_CONFIG } from "@stb/product-config"

import { TRIAL_DAYS } from "@/brand/plan-numbers"
import { organizationJsonLd } from "@/core/seo/json-ld"

import { DATA_COUNTRY } from "../../content/pages/trust-facts"

/** Copy source: docs/design/landing/copy.md section 2.1 (and the home design page). */
export const HOME_TITLE = "Mileage, receipts and invoices app"

export const HOME_SEO = {
  title: HOME_TITLE,
  description: `Track work trips with GPS, scan receipts and send tax invoices. For Australian sole traders. Data stored in ${DATA_COUNTRY}. ${TRIAL_DAYS}-day trial.`,
  // Organization only: no Offer until prices are final, no FAQPage (seo-spec).
  jsonLd: [organizationJsonLd()],
}

export const HOME_FINANCIAL_YEAR = "2026-27"
export const rate = centsPerKmRate(HOME_FINANCIAL_YEAR)

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"]

/** "2026-10-27" -> "27 Oct 2026" (string maths, so no timezone shift). */
export const formatCheckedOn = (iso: string): string => {
  const [y, m, d] = iso.split("-").map(Number)
  return `${d} ${MONTHS[(m ?? 1) - 1]} ${y}`
}

/** "2026-27" -> "2026–27" (en dash, as the ATO writes it). */
export const displayYear = (fy: string): string => fy.replace("-", "–")

export const capKm = CENTS_PER_KM_CAP_KM.toLocaleString("en-AU")

/** Illustrative trip in the hero card: the 12.4 km example from the design page. */
export const EXAMPLE_TRIP = {
  route: "Merrylands to Parramatta",
  km: "12.4",
  // 12.4 km = 124 / 10 km; integer cents via core/money.
  amount: formatMoney(multiplyByRatio(money(rate.cents, "AUD"), 124, 10), "en-AU").replace(
    "$",
    "A$",
  ),
}

export const HOME_JOBS = [
  {
    title: "Mileage logbook",
    href: "/mileage-tracker",
    body: "Trips are recorded by your phone's GPS. Compare cents per km with the logbook method and download your trip log as CSV.",
    tone: "success",
  },
  {
    title: "Receipts and expenses",
    href: "/receipt-scanner",
    body: "Photograph a receipt or upload a PDF. We read the supplier, date, total and GST. You check it before it counts.",
    tone: "primary",
  },
  {
    title: "Tax invoices and GST",
    href: "/invoicing",
    body: "Send tax invoices by email with a PDF, work out GST on each one and see who has paid.",
    tone: "violet",
  },
] as const

export const HOME_STEPS = [
  {
    title: "Install and sign in",
    body: "Use your email. No password to remember. Add your car and, if you have one, your ABN.",
  },
  {
    title: "Drive, snap, invoice",
    body: "Trips record in the background. Photograph receipts as you go. Create invoices on the web or phone.",
  },
  {
    title: "Export at tax time",
    body: "Download your trip log as CSV and your invoices as PDF, or export all your records as a zip. Send them to your accountant.",
  },
]

/**
 * Features that are not live yet. Home copy must not mention them (home-content.test.ts); remove
 * an entry here when the feature ships and the copy may claim it.
 */
export const UNRELEASED_FEATURE_TERMS = [/\bBAS\b/, /GST (and BAS )?summar/i, /logbook report/i]

/** ATO record keeping overview; read in full 2026-10-23 (docs/research/gps-retention-legal-basis.md). */
export const ATO_RECORDS_URL =
  "https://www.ato.gov.au/businesses-and-organisations/preparing-lodging-and-paying/record-keeping-for-business/overview-of-record-keeping-rules-for-business"
export const ATO_RECORDS_CHECKED = "2026-10-23"

/** Business-page twin of the individuals source; checked 2026-10-27 (copy.md header). */
export const ATO_BUSINESS_KM_URL =
  "https://www.ato.gov.au/businesses-and-organisations/income-deductions-and-concessions/income-and-deductions-for-business/deductions/deductions-for-motor-vehicle-expenses/cents-per-kilometre-method"

const { plans } = PRODUCT_CONFIG

/** Draft prices: docs/decisions.md D-PRICE-KEEP. Not final until the Owner confirms. GST wording removed until accountant confirms (T643). */
export const HOME_PLANS = [
  {
    name: "Free",
    price: formatAud(plans.free.monthlyCents),
    period: "",
    note: `${plans.free.tripsMonthly} GPS trips and ${plans.free.invoicesMonthly} invoices a month`,
  },
  {
    name: "Logbook",
    price: formatAud(plans.logbook.monthlyCents),
    period: " / month",
    note: "Unlimited GPS trips and the logbook method",
  },
  {
    name: "Pro",
    price: formatAud(plans.pro.monthlyCents),
    period: " / month",
    note: "Invoices with GST, and receipts",
    highlight: true,
  },
]

export const HOME_FAQ = [
  {
    question: "Does it replace my accountant?",
    answer:
      "No. It keeps your records tidy and exports them in formats an accountant can use. Deduction and GST figures are estimates, not tax advice.",
  },
  {
    question: "Do I need an ABN?",
    answer: "Not for the mileage logbook. Invoices and expenses need an ABN.",
  },
  {
    question: "What does the app do without signal?",
    answer: "Trips and receipts are saved on your phone and sync when you are back online.",
  },
]
